// ============================================================
// SALES REPORTS PAGE
// ============================================================

import React, { useState, useEffect } from 'react';
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Download, TrendingUp } from 'lucide-react';
import { Button, StatCard } from '../components/ui';
import { getRevenueChartData } from '../services/mockData';
import { adminApi } from '../services/apiService';
import { formatCurrency } from '../utils/helpers';

const SalesReports = () => {
  const [period, setPeriod] = useState('monthly');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    adminApi.getSalesReport({ period }, getRevenueChartData)
      .then(res => {
        const arr = Array.isArray(res) ? res : (res?.items || res?.data || []);
        // Normalize: backend returns {date, revenue, orders}; ensure month + commission fields
        setData(arr.map(d => ({
          ...d,
          month: d.month || d.date || d.period || '',
          revenue: d.revenue || 0,
          orders: d.orders || d.order_count || 0,
          commission: d.commission || Math.round((d.revenue || 0) * 0.15),
        })));
        setLoading(false);
      })
      .catch(e => { setError(e.message || 'Failed to load data'); setLoading(false); });
  }, [period]);

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Sales Reports</h1>
          <p className="admin-page-subtitle">Detailed sales analytics and reporting</p>
        </div>
        <div className="admin-page-actions">
          <select className="admin-form-control admin-form-select" value={period} onChange={e => setPeriod(e.target.value)} style={{ width: 160 }}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
          <Button variant="secondary" icon={<Download size={14} />}>Export PDF</Button>
          <Button variant="primary" icon={<Download size={14} />}>Export CSV</Button>
        </div>
      </div>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      {!loading && !error && data.length === 0 ? (
        <div className="admin-card" style={{ textAlign: 'center', padding: 60, color: 'var(--admin-text-muted)' }}>
          <TrendingUp size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
          <p style={{ fontSize: 16, fontWeight: 600 }}>No sales data yet</p>
          <p style={{ fontSize: 13, marginTop: 4 }}>Sales will appear here once orders are placed.</p>
        </div>
      ) : (
        <>
          <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
            <StatCard label="Total Sales" value={formatCurrency(data.reduce((s, d) => s + d.revenue, 0))} icon={<TrendingUp size={18} />} color="#E85D04" change={18.4} />
            <StatCard label="Total Orders" value={data.reduce((s, d) => s + d.orders, 0).toLocaleString()} icon={<TrendingUp size={18} />} color="#3B82F6" change={12.1} />
            <StatCard label="Commission" value={formatCurrency(data.reduce((s, d) => s + d.commission, 0))} icon={<TrendingUp size={18} />} color="#10B981" change={22.3} />
            <StatCard label="Avg Order Value" value={formatCurrency(data.reduce((s,d)=>s+d.orders,0) ? data.reduce((s,d)=>s+d.revenue,0)/data.reduce((s,d)=>s+d.orders,0) : 0)} icon={<TrendingUp size={18} />} color="#8B5CF6" change={5.7} />
          </div>

          <div className="admin-card">
            <div className="admin-card-header">
              <h3 className="admin-card-title">📈 Sales Trends</h3>
            </div>
            <div className="admin-card-body">
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'var(--admin-text-secondary)' }} />
                  <YAxis tick={{ fontSize: 12, fill: 'var(--admin-text-secondary)' }} tickFormatter={v => `$${v >= 1000000 ? (v/1000000).toFixed(1)+"M" : v >= 1000 ? (v/1000).toFixed(0)+"K" : v}`} />
                  <Tooltip formatter={(v, n) => [`$${v >= 1000000 ? (v/1000000).toFixed(2)+"M" : v >= 1000 ? (v/1000).toFixed(1)+"K" : v}`, n]} contentStyle={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', borderRadius: 8 }} />
                  <Legend />
                  <Bar dataKey="revenue" fill="#E85D04" radius={[4, 4, 0, 0]} name="Revenue" />
                  <Bar dataKey="commission" fill="#10B981" radius={[4, 4, 0, 0]} name="Commission" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="admin-card" style={{ marginTop: 24 }}>
            <div className="admin-card-header"><h3 className="admin-card-title">Sales Breakdown</h3></div>
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Period</th><th>Revenue</th><th>Orders</th><th>Commission</th>
                    <th>Avg Order Value</th><th>Growth</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={row.month || i}>
                      <td><b>{row.month}</b></td>
                      <td><b style={{ color: '#10B981' }}>{formatCurrency(row.revenue)}</b></td>
                      <td>{(row.orders || 0).toLocaleString()}</td>
                      <td>{formatCurrency(row.commission)}</td>
                      <td>{formatCurrency(row.orders > 0 ? Math.round(row.revenue / row.orders) : 0)}</td>
                      <td>
                        {i > 0 && data[i-1].revenue > 0 ? (
                          <span style={{ color: row.revenue > data[i-1].revenue ? '#10B981' : '#EF4444', fontWeight: 700 }}>
                            {row.revenue > data[i-1].revenue ? '↑' : '↓'}{' '}
                            {Math.abs(((row.revenue - data[i-1].revenue) / data[i-1].revenue) * 100).toFixed(1)}%
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SalesReports;
